import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Star, ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { getProducts, DEFAULT_PRODUCTS } from "@/lib/content";
import type { Product } from "@/types/content";

const CATEGORIES = ["Diapers", "Wipes", "Feeding", "Others"];

const ProductCarouselSection = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>(DEFAULT_PRODUCTS);
  const [activeCategory, setActiveCategory] = useState("Diapers");
  const [currentIndex, setCurrentIndex] = useState(10000); 
  const [displayText, setDisplayText] = useState<Product | null>(DEFAULT_PRODUCTS[0]);
  const [textOpacity, setTextOpacity] = useState(1);
  const prevOffsets = useRef<Record<string, number>>({});

  useEffect(() => {
    const handleUpdate = (data: Product[]) => {
      setProducts(data);
      if (data.length > 0) {
        setDisplayText((prev) => {
          if (!prev || prev.name === DEFAULT_PRODUCTS[0].name) {
            return data[0];
          }
          const updated = data.find(p => p.name === prev.name);
          return updated || data[0];
        });
      }
    };

    getProducts(handleUpdate).then(handleUpdate);
  }, []);

  const len = products.length;
  const safeIndex = len > 0 ? currentIndex % len : 0;
  const activeProduct = len > 0 ? products[safeIndex] : null;

  useEffect(() => {
    if (!activeProduct) return;
    setTextOpacity(0);
    const timeout = setTimeout(() => {
      setDisplayText(activeProduct);
      setTextOpacity(1);
    }, 400); 
    return () => clearTimeout(timeout);
  }, [activeProduct]);

  const handlePrev = () => {
    if (len === 0) return;
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (len === 0) return;
    setCurrentIndex((prev) => prev + 1);
  };

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setTouchEnd(null);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setTouchStart(clientX);
  };

  const onTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setTouchEnd(clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
  };



  return (
    <section className="py-16 md:py-24 bg-[#F8FDFD] relative overflow-hidden" id="products-list">
      <div className="container px-4 md:px-6">
        
        <div className="text-center mb-8 md:mb-10 space-y-2">
          <p className="text-[#006C73] font-black tracking-[0.3em] uppercase text-[10px] md:text-xs">Collection</p>
          <h2 className="text-xl sm:text-3xl md:text-7xl font-black text-[#003B46] tracking-tighter">Our Products</h2>
        </div>
 
        <div 
          className="text-center mb-6 md:mb-8 min-h-[120px] md:min-h-[160px] transition-all duration-500 ease-in-out"
          style={{ opacity: textOpacity, transform: `translateY(${textOpacity === 1 ? '0' : '15px'})` }}
        >
          <h3 className="text-xl sm:text-2xl md:text-5xl font-black text-[#003B46] mb-2 md:mb-4 tracking-tight px-4">
            {displayText.name}
          </h3>
          <div className="flex flex-col items-center gap-3 md:gap-6">
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-[10px] md:text-sm font-black">
              {displayText.tag && (
                <span className="text-[#006C73] uppercase tracking-[0.2em]">
                  {displayText.tag}
                </span>
              )}
              <div className="flex items-center gap-1.5 md:gap-2 text-[#FFB800]">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} size={12} fill="currentColor" strokeWidth={0} className="md:w-3.5 md:h-3.5" />
                  ))}
                </div>
                <span className="text-[#003B46] text-xs md:text-base">5.0</span>
              </div>
            </div>
 
            {displayText.tag?.trim().toLowerCase() === 'upcoming' ? (
              <button
                disabled
                className="w-full bg-gray-200 text-gray-500 py-3 rounded-full text-xs font-black uppercase tracking-widest cursor-not-allowed transition-all"
              >
                Coming Soon
              </button>
            ) : (
              <button
                onClick={() => {
                  addToCart(displayText.name, displayText.price);
                  navigate('/checkout');
                }}
                className="bg-[#004B57] text-white px-6 sm:px-8 md:px-10 py-3 sm:py-3.5 md:py-4 rounded-full font-black text-[10px] sm:text-[11px] md:text-sm uppercase tracking-widest hover:bg-[#006C73] hover:shadow-2xl hover:shadow-[#006C73]/30 transition-all active:scale-95 flex items-center gap-2 md:gap-3"
              >
                <ShoppingCart size={14} className="md:w-4 md:h-4" strokeWidth={3} />
                Order Now — ৳{displayText.price}
              </button>
            )}
          </div>
        </div>
 
        <div 
          className="relative w-full max-w-[1600px] mx-auto flex items-center justify-center min-h-[300px] md:min-h-[400px] overflow-hidden touch-pan-y"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseDown={onTouchStart}
          onMouseMove={(e) => touchStart && onTouchMove(e)}
          onMouseUp={onTouchEnd}
          onMouseLeave={onTouchEnd}
        >
          
          <button 
            onClick={handlePrev}
            className="hidden md:flex absolute top-[75%] -translate-y-1/2 left-[10%] md:left-[25%] lg:left-[28%] z-40 w-12 h-12 items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm text-[#004B57] hover:bg-gray-50 transition-all hover:scale-105"
          >
            <ChevronLeft size={24} />
          </button>
 
          <div className="relative w-full h-[250px] md:h-[350px] flex items-center justify-center [perspective:1000px]">
            {products.map((product, index) => {
              let offset = (index - safeIndex) % len;
              if (offset < 0) offset += len; 
              if (offset > Math.floor(len / 2)) offset -= len; 
              
              const prevOffset = prevOffsets.current[product.id || index];
              const isJump = prevOffset !== undefined && Math.abs(offset - prevOffset) > (len / 2);
              
              prevOffsets.current[product.id || index] = offset;
 
              const isActive = offset === 0;
              const isPrev = offset === -1;
              const isNext = offset === 1;
              const isFarPrev = offset === -2;
              const isFarNext = offset === 2;
 
              let translateX = '0%';
              let translateZ = '0px';
              let scale = 1;
              let opacity = 1;
              let blur = '0px';
              let zIndex = 0;
 
              const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
              const desktopSpacing = 140;
              const mobileSpacing = 100;
              const spacing = isMobile ? mobileSpacing : desktopSpacing;
 
              if (isActive) {
                translateX = '0%';
                translateZ = '0px';
                scale = 1;
                opacity = 1;
                blur = '0px';
                zIndex = 30;
              } else if (isPrev) {
                translateX = isMobile ? '0%' : `-${spacing}%`;
                translateZ = isMobile ? '0px' : '-450px';
                scale = isMobile ? 0.8 : 0.68;
                opacity = isMobile ? 0 : 0.6;
                blur = isMobile ? '0px' : '4px';
                zIndex = 10;
              } else if (isNext) {
                translateX = isMobile ? '0%' : `${spacing}%`;
                translateZ = isMobile ? '0px' : '-450px';
                scale = isMobile ? 0.8 : 0.68;
                opacity = isMobile ? 0 : 0.6;
                blur = isMobile ? '0px' : '4px';
                zIndex = 10;
              } else if (isFarPrev) {
                translateX = isMobile ? '0%' : `-${spacing * 1.8}%`;
                translateZ = isMobile ? '0px' : '-600px';
                scale = isMobile ? 0.8 : 0.5;
                opacity = 0;
                blur = isMobile ? '0px' : '8px';
                zIndex = 0;
              } else if (isFarNext) {
                translateX = isMobile ? '0%' : `${spacing * 1.8}%`;
                translateZ = isMobile ? '0px' : '-600px';
                scale = isMobile ? 0.8 : 0.5;
                opacity = 0;
                blur = isMobile ? '0px' : '8px';
                zIndex = 0;
              } else {
                translateX = '0%';
                translateZ = '-800px';
                scale = 0.3;
                opacity = 0;
                blur = isMobile ? '0px' : '12px';
                zIndex = -10;
              }
 
              const transitionStyle = isJump 
                ? 'none' 
                : 'all 1000ms cubic-bezier(0.4, 0, 0.2, 1)';
 
              return (
                <div
                  key={product.id || index}
                  className={`absolute top-1/2 -translate-y-1/2 w-48 h-48 md:w-80 md:h-80 cursor-pointer select-none [transform-style:preserve-3d]`}
                  onClick={() => {
                    if (isPrev) handlePrev();
                    if (isNext) handleNext();
                  }}
                  style={{
                    transition: transitionStyle,
                    transform: `translateY(-50%) translateX(${translateX}) translateZ(${translateZ}) scale(${scale})`,
                    opacity: opacity,
                    filter: isMobile ? undefined : `blur(${blur})`,
                    zIndex: zIndex,
                    pointerEvents: (isActive || isPrev || isNext || isFarPrev || isFarNext) ? 'auto' : 'none',
                  }}
                >
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    width="320"
                    height="320"
                    className="w-full h-full object-contain"
                    draggable="false"
                    loading={isActive ? "eager" : "lazy"}
                  />
                </div>
              );
            })}
          </div>
 
          <button 
            onClick={handleNext}
            className="hidden md:flex absolute top-[75%] -translate-y-1/2 right-[10%] md:right-[25%] lg:right-[28%] z-40 w-12 h-12 items-center justify-center rounded-full bg-white border border-gray-200 shadow-sm text-[#004B57] hover:bg-gray-50 transition-all hover:scale-105"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProductCarouselSection;

